import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Sparkles, Gift, Phone, Mail, Building2, Check, DollarSign } from "lucide-react";
import { ProductHeader, ProductFooter } from "@/pages/Products";
import { API } from "@/App";
import { CRAVY_CONFIG } from "@/config";

const STEPS = [
  { n: "01", t: "Refer a property",        d: "Drop us their name and a contact you know — apartments, offices, hotels, gyms, anywhere Cravy belongs." },
  { n: "02", t: "We reach out",            d: "We handle the outreach with a professional intro, mention you sent them, and walk them through Cravy." },
  { n: "03", t: "They sign up",            d: "When they install their first market, you've officially referred a property." },
  { n: "04", t: `You earn $${CRAVY_CONFIG.referralBonus}`, d: `We send you a $${CRAVY_CONFIG.referralBonus} thank-you within 14 days of install. No cap on how many properties you can refer.` },
];

export default function Refer() {
  const [form, setForm] = useState({
    referrer_name: "", referrer_email: "", referrer_phone: "",
    property_name: "", property_contact: "", property_city: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    document.title = "Refer a Property — Earn with Cravy";
    window.scrollTo(0, 0);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/referrals`, form);
      setDone(true);
      toast.success("Referral sent! We'll take it from here.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="font-body bg-cravy-cream min-h-screen">
      <ProductHeader />

      {/* Hero */}
      <section className="bg-cravy-ink cravy-grain pt-[72px] relative overflow-hidden">
        <div className="absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full bg-cravy-orange/15 blur-[120px]" />
        <div className="absolute -bottom-40 -left-32 w-[420px] h-[420px] rounded-full bg-cravy-orangeDeep/10 blur-[120px]" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-16 lg:pt-24 pb-16 lg:pb-20">
          <Link to="/" className="text-cravy-cream/60 hover:text-cravy-orange text-[13px] inline-flex items-center gap-2" data-testid="refer-back">← Back to home</Link>
          <div className="mt-6 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cravy-orange/10 border border-cravy-orange/25 text-cravy-orange text-[12px] font-semibold tracking-wide uppercase">
            <Gift className="w-3.5 h-3.5" /> Refer-a-Property Program
          </div>
          <h1 className="mt-6 font-display font-extrabold text-cravy-cream tracking-[-0.03em] leading-[0.95] text-[44px] sm:text-[64px] lg:text-[80px] max-w-4xl">
            Know a property that <span className="italic text-cravy-orange">should have Cravy?</span>
          </h1>
          <p className="mt-6 text-cravy-cream/75 text-[17px] lg:text-[19px] max-w-2xl leading-relaxed">
            Refer them. We do the work. When they install, you get <strong className="text-cravy-orange">${CRAVY_CONFIG.referralBonus}</strong>. No cap. No catch.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-cravy-cream py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="text-cravy-orangeDeep font-semibold text-[12px] uppercase tracking-[0.25em]">How it works</div>
          <h2 className="mt-3 font-display font-extrabold text-[36px] sm:text-[48px] leading-[1] tracking-[-0.03em] mb-12">
            Four steps. About 90 seconds.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map((s, i) => (
              <div key={i} className="bg-white rounded-3xl p-6 border border-cravy-ink/5 hover:border-cravy-orange/40 hover:-translate-y-1 transition-all" data-testid={`refer-step-${i}`}>
                <div className="font-display font-extrabold text-cravy-orange text-[42px] leading-none">{s.n}</div>
                <div className="mt-5 font-display font-bold text-[19px] tracking-tight">{s.t}</div>
                <div className="mt-2 text-cravy-ink/70 text-[14px] leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Referral Form */}
      <section className="bg-cravy-ink cravy-grain py-24 lg:py-28 relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-[420px] h-[420px] rounded-full bg-cravy-orangeDeep/12 blur-[140px]" />
        <div className="absolute -bottom-20 -right-20 w-[420px] h-[420px] rounded-full bg-cravy-orange/12 blur-[140px]" />
        <div className="relative max-w-3xl mx-auto px-5 lg:px-10">
          {done ? (
            <div className="text-center rounded-3xl border border-white/8 bg-white/[0.03] p-12 backdrop-blur-sm" data-testid="refer-done">
              <div className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-cravy-orange text-cravy-ink mb-5">
                <Check className="w-8 h-8" strokeWidth={3} />
              </div>
              <h2 className="font-display font-extrabold text-cravy-cream text-[36px] sm:text-[48px] tracking-[-0.03em] leading-[0.95]">
                Got it. We'll take it from here.
              </h2>
              <p className="mt-6 text-cravy-cream/70 text-[16px] leading-relaxed max-w-md mx-auto">
                We'll reach out to your referral within one business day. You'll get an update when they sign up — and a check for <strong className="text-cravy-orange">${CRAVY_CONFIG.referralBonus}</strong> when they install.
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <button onClick={() => { setDone(false); setForm({ referrer_name: form.referrer_name, referrer_email: form.referrer_email, referrer_phone: form.referrer_phone, property_name: "", property_contact: "", property_city: "", notes: "" }); }} className="btn-orange inline-flex items-center gap-2 px-6 py-3 rounded-full text-[14px]" data-testid="refer-another">
                  Refer another property <ArrowRight className="w-4 h-4" />
                </button>
                <Link to="/" className="btn-ghost-dark inline-flex items-center gap-2 px-6 py-3 rounded-full text-[14px] font-semibold">
                  Back to home
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-white/8 bg-white/[0.03] p-7 lg:p-9 backdrop-blur-sm" data-testid="refer-form">
              <div className="mb-7">
                <div className="text-cravy-orange font-semibold text-[12px] uppercase tracking-[0.25em]">Tell us who to reach out to</div>
                <h2 className="mt-3 font-display font-extrabold text-cravy-cream text-[28px] sm:text-[36px] tracking-[-0.02em] leading-[1]">
                  Send us your referral
                </h2>
              </div>

              <div className="text-cravy-cream/70 text-[11px] uppercase tracking-[0.15em] font-semibold mb-3">Your info</div>
              <div className="grid sm:grid-cols-2 gap-4 mb-7">
                <Field label="Your name *"><input required value={form.referrer_name} onChange={update("referrer_name")} className="cravy-input" placeholder="Jane Smith" data-testid="refer-name" /></Field>
                <Field label="Your email *"><input required type="email" value={form.referrer_email} onChange={update("referrer_email")} className="cravy-input" placeholder="jane@email.com" data-testid="refer-email" /></Field>
                <Field label="Your phone"><input value={form.referrer_phone} onChange={update("referrer_phone")} className="cravy-input" placeholder="(555) 123-4567" data-testid="refer-phone" /></Field>
              </div>

              <div className="text-cravy-cream/70 text-[11px] uppercase tracking-[0.15em] font-semibold mb-3">Property you're referring</div>
              <div className="grid sm:grid-cols-2 gap-4 mb-7">
                <Field label="Property name *"><input required value={form.property_name} onChange={update("property_name")} className="cravy-input" placeholder="The Bowery Lofts" data-testid="refer-property" /></Field>
                <Field label="City"><input value={form.property_city} onChange={update("property_city")} className="cravy-input" placeholder="Austin, TX" data-testid="refer-city" /></Field>
                <div className="sm:col-span-2">
                  <Field label="Property contact (if you know one)"><input value={form.property_contact} onChange={update("property_contact")} className="cravy-input" placeholder="Name, role, email or phone" data-testid="refer-contact" /></Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Notes (optional)"><textarea rows="3" value={form.notes} onChange={update("notes")} className="cravy-input" placeholder="Anything that'd help us connect — best time to call, the angle they'd love, etc." data-testid="refer-notes" /></Field>
                </div>
              </div>

              <button type="submit" disabled={submitting} className="btn-orange inline-flex items-center gap-2 px-7 py-4 rounded-full text-[15px] disabled:opacity-60" data-testid="refer-submit">
                {submitting ? "Sending…" : "Send referral"} <ArrowRight className="w-4 h-4" />
              </button>
              <p className="mt-4 text-cravy-cream/45 text-[12px]">
                By submitting you confirm you're comfortable with us reaching out to your referral and mentioning you sent us.
              </p>
            </form>
          )}
        </div>
      </section>

      <ProductFooter />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-cravy-cream/70 text-[12px] uppercase tracking-[0.15em] mb-2 font-semibold">{label}</span>
      {children}
    </label>
  );
}
